import {computed, onBeforeUnmount, ref, watch} from 'vue'
import {ApiError} from '../api/client'
import {scriptsApi} from '../api/scripts'
import {draftOf, parseDraft, type ScriptDirectory, type ScriptDocument, type ScriptDraft, type ScriptSummary} from '../types/scripts'

export function useScriptWorkspace(accountId: string) {
  const directory = ref<ScriptDirectory | null>(null)
  const script = ref<ScriptDocument | null>(null)
  const loading = ref(false), saving = ref(false), error = ref(''), conflict = ref(false), storageError = ref(false)
  const recovery = ref<{revision: number; draft: ScriptDraft} | null>(null)
  const query = ref(''), baseline = ref('')
  const serializeDraft = (doc: ScriptDocument) => {const draft = draftOf(doc); return JSON.stringify(parseDraft(draft) ?? draft)}
  const serialized = computed(() => script.value ? serializeDraft(script.value) : '')
  const dirty = computed(() => !!script.value && serialized.value !== baseline.value)
  let timer: number | undefined, listing = 0, pending: Promise<boolean> | null = null, flushing: Promise<boolean> | null = null
  const message = (e: unknown) => e instanceof Error ? e.message : '暂时无法保存，请稍后重试'
  function localKey(id: string) {return `frame:scripts:draft:${directory.value?.workspace.key}:${accountId}:${id}`}
  function cacheDraft() {
    if (!script.value?.canEdit || !directory.value || recovery.value) return
    try {
      if (dirty.value) localStorage.setItem(localKey(script.value.id), JSON.stringify({revision: script.value.revision, draft: draftOf(script.value)}))
      else localStorage.removeItem(localKey(script.value.id))
      storageError.value = false
    } catch {storageError.value = true}
  }
  function updateSummary(doc: ScriptDocument) {
    if (!directory.value) return
    const item: ScriptSummary = {...doc, sceneCount: doc.scenes.length, totalSeconds: doc.scenes.reduce((n, s) => n + s.seconds, 0)}
    const index = directory.value.items.findIndex(row => row.id === doc.id)
    if (index >= 0) directory.value.items.splice(index, 1, item)
    else if (!query.value) directory.value.items.unshift(item)
  }
  async function loadDirectory(append = false) {
    const generation = ++listing
    const result = await scriptsApi.list(query.value, append ? directory.value?.nextCursor ?? '' : '')
    if (generation !== listing) return
    const sameSpace = !directory.value || directory.value.workspace.key === result.workspace.key
    if (!sameSpace) {
      cacheDraft(); window.clearTimeout(timer)
      script.value = null; baseline.value = ''; recovery.value = null; conflict.value = false
      error.value = '创作空间已变更，请从当前空间重新选择脚本。原空间的未保存草稿仍保留在本机。'
    }
    if (append && directory.value && sameSpace) result.items = [...directory.value.items, ...result.items.filter(row => !directory.value!.items.some(old => old.id === row.id))]
    directory.value = result
  }
  function accept(doc: ScriptDocument) {
    window.clearTimeout(timer)
    baseline.value = serializeDraft(doc)
    script.value = doc
    conflict.value = false; error.value = ''; recovery.value = null
    try {
      const raw = localStorage.getItem(localKey(doc.id))
      if (raw && raw.length <= 3 * 1024 * 1024) {
        const cached = JSON.parse(raw)
        const draft = parseDraft(cached?.draft)
        if (Number.isInteger(cached?.revision) && cached.revision > 0 && draft && JSON.stringify(draft) !== baseline.value) recovery.value = {revision: cached.revision, draft}
      }
    } catch {storageError.value = true}
    updateSummary(doc)
  }
  async function save(): Promise<boolean> {
    if (pending) return pending
    if (!script.value || !dirty.value) return true
    if (!script.value.canEdit || conflict.value || recovery.value) return false
    window.clearTimeout(timer)
    const current = script.value, draft = draftOf(current), sent = serializeDraft(current), space = directory.value?.workspace.key
    saving.value = true; error.value = ''
    pending = (async () => {
      try {
        const {script: saved} = await scriptsApi.save(current.id, current.revision, draft)
        if (script.value?.id !== current.id || directory.value?.workspace.key !== space) return false
        const keepEdits = serialized.value !== sent
        baseline.value = serializeDraft(saved)
        script.value = keepEdits ? {...saved, ...draftOf(script.value)} : saved
        updateSummary(saved); cacheDraft()
        return true
      } catch (e) {
        if (script.value?.id !== current.id || directory.value?.workspace.key !== space) return false
        error.value = message(e)
        conflict.value = e instanceof ApiError && e.code === 'CONFLICT'
        cacheDraft()
        return false
      } finally {
        saving.value = false; pending = null
        if (dirty.value && !error.value && !conflict.value) schedule()
      }
    })()
    return pending
  }
  /** Wait for every acknowledged edit before export. The caller locks editor UI. */
  async function saveAll(): Promise<boolean> {
    if (flushing) return flushing
    if (!script.value || conflict.value || recovery.value) return false
    const documentId = script.value.id, space = directory.value?.workspace.key
    flushing = (async () => {
      await Promise.resolve() // Assign flushing before a clean document can finish.
      try {
        // A UI lock makes this normally one or two passes. Keep an upper bound
        // for programmatic edits or an integration that forgot to lock input.
        for (let attempt = 0; attempt < 16; attempt++) {
          window.clearTimeout(timer)
          if (script.value?.id !== documentId || directory.value?.workspace.key !== space || conflict.value || recovery.value) return false
          if (pending && !await pending) return false
          if (script.value?.id !== documentId || directory.value?.workspace.key !== space || conflict.value || recovery.value) return false
          if (!dirty.value) return true
          if (!await save()) return false
          if (!dirty.value) return true
        }
        window.clearTimeout(timer)
        error.value = '内容仍在持续变化，请暂停编辑后重新保存或导出。'
        cacheDraft(); return false
      } finally {flushing = null}
    })()
    return flushing
  }
  function schedule() {
    window.clearTimeout(timer)
    if (script.value?.canEdit && dirty.value && !conflict.value && !recovery.value && !saving.value) timer = window.setTimeout(() => {void save()}, 1400)
  }
  watch(serialized, () => {cacheDraft(); schedule()})
  async function open(id: string) {
    if (loading.value) return false
    loading.value = true
    const space = directory.value?.workspace.key
    try {
      if (pending && !await pending) return false
      if (!await save()) return false
      const doc = (await scriptsApi.detail(id)).script
      if (directory.value?.workspace.key !== space) return false
      accept(doc); return true
    }
    catch (e) {error.value = message(e); return false}
    finally {loading.value = false}
  }
  async function create(title: string, projectId = '') {
    if (loading.value) return false
    loading.value = true
    const space = directory.value?.workspace.key
    try {
      if (pending && !await pending) return false
      if (!await save()) return false
      const doc = (await scriptsApi.create(title, projectId)).script
      if (directory.value?.workspace.key !== space) return false
      accept(doc); return true
    }
    catch (e) {error.value = message(e); return false}
    finally {loading.value = false}
  }
  async function saveCopy() {
    if (!script.value || loading.value) return false
    loading.value = true
    const space = directory.value?.workspace.key
    window.clearTimeout(timer)
    try {
      if (pending) await pending
      if (!script.value || directory.value?.workspace.key !== space) return false
      const original = script.value, draft = draftOf(original)
      const projectId = directory.value?.projects.some(p => p.id === draft.projectId) ? draft.projectId : ''
      const fresh = (await scriptsApi.create(`${draft.title.slice(0, 110)} · 副本`, projectId)).script
      if (directory.value?.workspace.key !== space) return false
      const {script: saved} = await scriptsApi.save(fresh.id, fresh.revision, {...draft, title: fresh.title, projectId})
      if (directory.value?.workspace.key !== space) return false
      accept(saved); return true
    } catch (e) {error.value = message(e); return false}
    finally {loading.value = false}
  }
  function recover() {
    if (!script.value || !recovery.value) return
    const cached = recovery.value
    script.value = {...script.value, ...cached.draft}
    if (cached.revision !== script.value.revision) {
      conflict.value = true
      error.value = '本机草稿对应的版本较旧，请另存副本，或先比较最新版本。'
    }
    recovery.value = null; cacheDraft(); schedule()
  }
  function dismissRecovery() {
    recovery.value = null
    try {if (script.value) localStorage.removeItem(localKey(script.value.id))} catch {storageError.value = true}
    cacheDraft(); schedule()
  }
  async function refresh() {
    if (!script.value || dirty.value || saving.value || loading.value || recovery.value) return false
    loading.value = true
    const space = directory.value?.workspace.key, documentId = script.value.id
    try {
      const doc = (await scriptsApi.detail(documentId)).script
      if (directory.value?.workspace.key !== space || script.value?.id !== documentId) return false
      accept(doc); return true
    }
    catch (e) {error.value = message(e); return false}
    finally {loading.value = false}
  }
  function exportText() {
    const s = script.value
    if (!s) return
    const text = [s.title, `版本 ${s.revision}${dirty.value ? '（含未保存修改）' : ''}`, `创作概要：${s.logline}`, `受众 / 用途：${s.audience}`, '', ...s.scenes.flatMap((shot, i) => [`镜头 ${String(i + 1).padStart(2, '0')} · ${shot.heading || '未命名场景'}`, `地点：${shot.location} | 景别：${shot.shotSize} | 运镜：${shot.movement} | 时长：${shot.seconds} 秒 | ${shot.filmed ? '已拍摄' : '待拍摄'}`, `画面：${shot.visual}`, `台词 / 声音：${shot.dialogue}`, `拍摄备注：${shot.notes}`, ''])].join('\n')
    const url = URL.createObjectURL(new Blob(['\uFEFF' + text], {type: 'text/plain;charset=utf-8'}))
    const link = document.createElement('a'); link.href = url; link.download = `${s.title.replace(/[\\/:*?"<>|]/g, '_') || '帧阅脚本'}.txt`; link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  const beforeLeave = (event: BeforeUnloadEvent) => {cacheDraft(); if (dirty.value) {event.preventDefault(); event.returnValue = ''}}
  window.addEventListener('beforeunload', beforeLeave)
  onBeforeUnmount(() => {window.clearTimeout(timer); cacheDraft(); window.removeEventListener('beforeunload', beforeLeave)})
  return {directory, script, loading, saving, error, conflict, recovery, storageError, query, dirty, loadDirectory, open, create, save, saveAll, flush: saveAll, saveCopy, recover, dismissRecovery, refresh, exportText}
}
